import { useEffect } from 'react'

interface ClearAllConfirmProps {
  title: string
  body: string
  confirmLabel: string
  cancelLabel: string
  busy: boolean
  /** Why the bulk resolve was rejected — the dialog stays open so it can be retried. */
  error?: string
  onConfirm: () => void
  onClose: () => void
}

/**
 * Minimal inline confirm for "Clear map pins". Hand-rolled fixed-overlay modeled on the sibling
 * EnrollFaceModal (backdrop click + Escape close, inner stopPropagation). Kept co-located rather
 * than reusing a forms/cctv-scoped dialog so the log stays self-contained.
 */
export function ClearAllConfirm({
  title,
  body,
  confirmLabel,
  cancelLabel,
  busy,
  error,
  onConfirm,
  onClose,
}: ClearAllConfirmProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }

    window.addEventListener('keydown', onKey)

    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div
      data-testid="clear-all-confirm"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 p-4"
      onClick={busy ? undefined : onClose}
    >
      <div
        className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-gray-900"
        onClick={e => e.stopPropagation()}
      >
        <h3 className="mb-3 text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
        <p className="mb-6 text-sm text-gray-600 dark:text-gray-400">{body}</p>
        {error && (
          <p
            role="alert"
            className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300"
          >
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="rounded bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
